import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface PagedListOptions {
  // Controlled page (1-based), e.g. read from `?page=` in the URL. The hook
  // then keeps no page of its own and reports every change, including its
  // own resets and clamps, through onPageChange. Always pass a number
  // (`Number(param) || 1`): undefined makes the hook uncontrolled.
  page?: number;
  onPageChange?: (page: number) => void;
}

export interface PagedList<T> {
  // 1-based, always between 1 and pageCount.
  page: number;
  setPage: (page: number) => void;
  // At least 1, so an empty list still has a page 1.
  pageCount: number;
  pageItems: T[];
  // 1-based positions of the first and last item shown ("Showing 21–40");
  // both 0 when the list is empty.
  from: number;
  to: number;
  total: number;
}

const sameKeys = (a: ReadonlyArray<unknown>, b: ReadonlyArray<unknown>) =>
  a.length === b.length && a.every((key, i) => Object.is(key, b[i]));

// Client-side pagination of a list that is already in memory. Goes back to
// page 1 whenever one of `resetKeys` changes (search text, filters, sort),
// and pulls a page that is past the end back to the last page when the list
// shrinks. While the list is empty (e.g. reloading) the page is kept.
export function usePagedList<T>(
  items: T[],
  pageSize: number,
  resetKeys: ReadonlyArray<unknown> = [],
  options: PagedListOptions = {},
): PagedList<T> {
  const { page: controlledPage, onPageChange } = options;
  const controlled = controlledPage !== undefined;
  const [ownPage, setOwnPage] = useState(1);
  const requested = controlledPage ?? ownPage;

  const setPage = useCallback(
    (next: number) => {
      if (controlled) onPageChange?.(next);
      else setOwnPage(next);
    },
    [controlled, onPageChange],
  );

  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  // Compared with the keys of the last committed render, so the render that
  // brings new keys already shows page 1.
  const committedKeys = useRef(resetKeys);
  const keysChanged = !sameKeys(committedKeys.current, resetKeys);
  const valid = Number.isInteger(requested) && requested >= 1 ? requested : 1;
  const page = keysChanged ? 1 : Math.min(valid, pageCount);

  // The last correction reported, until the requested page catches up.
  const reported = useRef<{ requested: number; page: number } | null>(null);

  // Store what this render showed: the reset, or the clamp once the list is
  // loaded. Each correction is reported once: a controlled page that cannot
  // take it (e.g. a URL that drops `page=1` and reads back 0) would otherwise
  // be written again after every render.
  useEffect(() => {
    committedKeys.current = resetKeys;
    if (page === requested) {
      reported.current = null;
      return;
    }
    if (!keysChanged && total === 0) return;
    const last = reported.current;
    if (last && Object.is(last.requested, requested) && last.page === page) {
      return;
    }
    reported.current = { requested, page };
    setPage(page);
  });

  const pageItems = useMemo(
    () => items.slice((page - 1) * pageSize, page * pageSize),
    [items, page, pageSize],
  );

  return {
    page,
    setPage,
    pageCount,
    pageItems,
    from: total ? (page - 1) * pageSize + 1 : 0,
    to: Math.min(page * pageSize, total),
    total,
  };
}
